import type { ComponentProps } from '@enonic/react-components'
import * as React from 'react'
import { Section, Container, ButtonLink } from '../primitives'
import { Icon, type IconName } from '../primitives/Icon'

export interface OmvendtUndervisningData extends Record<string, unknown> {
  heading: string
  lead: string
  bottomHeading: string
  bottomLead: string
  readMoreUrl?: string
  readMoreLabel: string
  readMoreNewTab: boolean
}

interface BoxItem {
  icon: IconName
  label: string
  description: string
}

interface BoxData {
  title: string
  items: [BoxItem, BoxItem]
}

const homeBox: BoxData = {
  title: 'Hjemme',
  items: [
    {
      icon: 'computer',
      label: 'Forberedelse',
      description: 'Videoer, podcaster, artikler og pensum',
    },
    {
      icon: 'schedule',
      label: 'Fleksibel læring',
      description: 'Læring i eget tempo før samling',
    },
  ],
}

const gatheringBox: BoxData = {
  title: 'Samlinger',
  items: [
    {
      icon: 'forum',
      label: 'Faglig refleksjon',
      description: 'Diskusjon og refleksjon sammen med lærer og medstudenter',
    },
    {
      icon: 'group',
      label: 'Praktisk anvendelse',
      description: 'Øvelser og casearbeid som bygger på forberedelsene hjemme',
    },
  ],
}

const Box = ({
  data,
  className = '',
}: {
  data: BoxData
  className?: string
}) => (
  <div className={`bg-blue-900 px-8 py-10 md:p-12 h-full ${className}`}>
    <h3 className="text-center text-2xl font-semibold uppercase tracking-wide text-white md:text-4xl">
      {data.title}
    </h3>
    <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[1fr_1px_1fr] md:items-center md:gap-6">
      <BoxItemRow item={data.items[0]} />
      <div
        aria-hidden="true"
        className="hidden md:block h-32 w-px bg-white/40"
      />
      <BoxItemRow item={data.items[1]} />
    </div>
  </div>
)

const BoxItemRow = ({ item }: { item: BoxItem }) => (
  <div className="relative z-20 flex flex-col items-center text-center text-white">
    <Icon name={item.icon} size={48} />
    <p className="mt-5 text-lg font-semibold uppercase tracking-wide md:text-xl">
      {item.label}
    </p>
    <p className="mt-2 max-w-[260px] text-base md:text-lg">
      {item.description}
    </p>
  </div>
)

const ConnectorShape = ({ className }: { className?: string }) => (
  <svg
    width="109"
    height="120"
    viewBox="0 0 109 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M45 0C45 5.52285 49.4772 10 55 10C60.5228 10 65 5.52285 65 0H109V120H65C65 114.477 60.5228 110 55 110C49.4772 110 45 114.477 45 120H0V0H45Z"
      fill="currentColor"
    />
  </svg>
)

const ConnectorVertical = () => (
  <div
    aria-hidden="true"
    className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-27.25 items-center justify-center text-blue-900"
  >
    <ConnectorShape />
    <div className="absolute flex items-center gap-4 text-white">
      <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor">
        <polygon points="0,0 14,10 0,20" />
      </svg>
      <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor">
        <polygon points="14,0 14,20 0,10" />
      </svg>
    </div>
  </div>
)

const ConnectorHorizontal = () => (
  <div
    aria-hidden="true"
    className="md:hidden flex absolute left-1/2 top-[48.7%] -translate-x-1/2 -translate-y-1/2 z-10 h-27.25 w-30 items-center justify-center text-blue-900"
  >
    <ConnectorShape className="rotate-90" />
    <div className="absolute flex flex-col items-center gap-4 text-white">
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <polygon points="0,0 20,0 10,14" />
      </svg>
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <polygon points="10,0 20,14 0,14" />
      </svg>
    </div>
  </div>
)

export const OmvendtUndervisning = (props: ComponentProps) => {
  const {
    heading,
    lead,
    bottomHeading,
    bottomLead,
    readMoreUrl,
    readMoreLabel,
    readMoreNewTab,
  } = props.data as OmvendtUndervisningData

  return (
    <Section>
      <Container>
        <div className="col-span-full md:col-span-8 flex flex-col gap-4">
          {heading && (
            <h2 className="font-serif text-3xl leading-tight text-blue-900 md:text-5xl">
              {heading}
            </h2>
          )}
          {lead && <p className="text-lg text-black md:text-2xl">{lead}</p>}
        </div>

        <div className="col-span-full mt-12 relative flex flex-col items-stretch gap-5 md:flex-row md:items-stretch md:gap-5">
          <div className="md:flex-1">
            <Box data={homeBox} className="rounded-2xl" />
          </div>
          <div className="md:flex-1">
            <Box data={gatheringBox} className="rounded-2xl" />
          </div>
          <ConnectorVertical />
          <ConnectorHorizontal />
        </div>

        {(bottomHeading || bottomLead) && (
          <div className="col-span-full md:col-span-8 mt-12 flex flex-col gap-4">
            {bottomHeading && (
              <h3 className="font-serif text-2xl leading-tight text-blue-900 md:text-4xl">
                {bottomHeading}
              </h3>
            )}
            {bottomLead && (
              <p className="text-lg text-black md:text-xl">{bottomLead}</p>
            )}
          </div>
        )}

        {readMoreLabel && readMoreUrl && (
          <div className="col-span-full mt-8">
            <ButtonLink
              href={readMoreUrl}
              label={readMoreLabel}
              newTab={readMoreNewTab}
              variant="text-link"
            />
          </div>
        )}
      </Container>
    </Section>
  )
}
